// Core
import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Box, Button, Flex, Image, Grid, Text, Skeleton, useColorMode, useBreakpointValue, useColorModeValue} from '@chakra-ui/react';
import { NavLink, useLocation, useHistory } from 'react-router-dom';
import Confetti from 'react-confetti';

// Assets
import { apiService } from 'services/ApiService';
import { maskEmail, getRandomNames } from 'services/Utils';
import { ReactComponent as DailyCursorIcon } from 'assets/svg/daily-cursor.svg';
import { ReactComponent as DailyWheelLiners } from 'assets/svg/daily-wheel-liners.svg';
import { ReactComponent as DailyWheelLinersWhite } from 'assets/svg/daily-wheel-liners-white.svg';
import { ReactComponent as DailyWheelIcon } from 'assets/svg/daily-wheel.svg';
import { ReactComponent as DailyWheelWhiteIcon } from 'assets/svg/daily-wheel-white.svg';
import { ReactComponent as DailyTopWheel1Icon } from 'assets/svg/daily-top-wheel-1.svg';
import { ReactComponent as DailyTopWheel2Icon } from 'assets/svg/daily-top-wheel-2.svg';
import logo from 'assets/img/logo.png';
import dailyWheelFooter from 'assets/img/daily-wheel-footer.png';
import autoDrawRegularSound from 'assets/sounds/auto-draw-regular.mp3';
import autoDrawSound from 'assets/sounds/auto-draw.mp3';
import winnerSound from 'assets/sounds/winner-sound.mp3';

// Components
import Giveaways from './Giveaway';
import EmptyList from './EmptyList';
import Countdown from 'components/Timers/Countdown';
import Numdown from 'components/Timers/Numdown';
import { StarsIcon, StarsWhiteIcon, SoundOnIcon, SoundOffIcon } from '../../../components/Icons/Icons';

const DailyWheel = ({ userData }) => {
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const isTestMode = queryParams.get('testMode') === 'true';

	const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const colorIcon = useColorModeValue('#0E1633', '#9FABCB');

    const history = useHistory();

    const responsiveBottomWheel = useBreakpointValue({
        base: 'calc(-75% - 150px)', 
        md: 'calc(-50% - 200px)' 
    });

    const responsiveBorderCounter = useBreakpointValue({
        base: '3px', 
        md: '6px' 
    });

    const responsiveHeader = useBreakpointValue({
        base: true, 
        md: false 
    });

    const [maskWinners, setMaskWinners] = useState(getRandomNames(12));

	useEffect(() => {
	    const interval = setInterval(() => {
	      setMaskWinners(getRandomNames(12));
	    }, 1000); 

	    return () => clearInterval(interval);
	}, []);

    const [data, setData] = useState([]);
    const [giveawayId, setGiveawayId] = useState();
    const [lastStep, setLastStep] = useState(false);

    const wheelRef = useRef();

    const Stars = useMemo(() => {
	  return isDark ? <StarsWhiteIcon fontSize={{ base: '18px', md: '40px' }} overflow="visible" />
	                : <StarsIcon className="daily-wheel-svg-white-theme" fontSize={{ base: '18px', md: '40px' }} overflow="visible" />;
	}, [isDark]);

    const { data: nextDaily, isLoading: isNextLoading } = useQuery({
        queryKey: ['dailyNextWinners'],
        queryFn: async () => {
            const response = await apiService.getGiveaways({ 
                status: 'active', 
                type: 'member',
                fromDeclareDate: new Date().toISOString(),
                declareSetting: 'auto' 
            });

            if(response?.data?.length === 0){
            	history.push('/giveaways');
            }

            if(isTestMode && window.location.hostname !== 'lmctplus.com'){
	            response.data[0].declareDate = new Date(new Date().getTime() + 12 * 1000);
	        }
            return response.data;
        },
        refetchOnWindowFocus: false,
        retry: false
    });

    useEffect(() => {
	  if (nextDaily?.[0]?.id) {
	    setGiveawayId(nextDaily[0].id);
	  }
	}, [nextDaily]);

    const [timeLeft, setTimeLeft] = useState('');
    const [winner, setWinner] = useState(null);
    const [winnerEmail, setWinnerEmail] = useState(null);
	const [isSpinning, setIsSpinning] = useState(false);

	useEffect(() => {
	  let retryInterval;

	  const fetchWinner = async () => {
	    try {
	      const response = await apiService.getGiveaway({ id: isTestMode && window.location.hostname !== 'lmctplus.com' ? 323 : giveawayId });
	      const fetchedWinner = response.data.prizes[0]?.winner;

	      if (fetchedWinner?.id) {
	        setWinner(fetchedWinner.firstName + ' ' + fetchedWinner.lastName);
	        setWinnerEmail(maskEmail(fetchedWinner.email));
	        clearInterval(retryInterval);
	        setTimeout(() => {
		      setLastStep(true);
		    }, 2000);
	      } else {
	        retryInterval = setTimeout(fetchWinner, 1000);
	      }
	    } catch (err) {
	      retryInterval = setTimeout(fetchWinner, 4000); 	
	      console.error(err);
	    }
	  };

	  if (timeLeft === '0' && !winner) {
	    setIsSpinning(true); 
	    const timer = setTimeout(() => {
	      setIsSpinning(false);
	      fetchWinner();
	    }, 3000);

	    return () => {
	      clearTimeout(timer);
	      clearInterval(retryInterval);
	    };
	  }
	}, [timeLeft]);

	useEffect(() => {
	  if (!isSpinning && winner && wheelRef.current) {
	    const computedStyle = window.getComputedStyle(wheelRef.current);
	    const transform = computedStyle.transform;

	    if (transform && transform !== 'none') {
	      const values = transform.split('(')[1].split(')')[0].split(',');
	      const a = parseFloat(values[0]);
	      const b = parseFloat(values[1]);
	      let angle = Math.round(Math.atan2(b, a) * (180 / Math.PI));
	      if (angle < 0) { angle += 360; }

	      wheelRef.current.style.setProperty('--current-rotate', `${angle}deg`);
	    }
	  }
	}, [isSpinning, winner]);

	const regularAudioRef = useRef(null);
	const drawAudioRef = useRef(null);
	const winAudioRef = useRef(null);

	const [soundOn, setSoundOn] = useState(true);
	const [userHasInteracted, setUserHasInteracted] = useState(true);

	useEffect(() => {
	  if (userHasInteracted && timeLeft !== '0') {
	    const playPromise = regularAudioRef.current?.play();
	    if (playPromise !== undefined) {
	      playPromise.catch((error) => {
	        console.warn('regular audio blocked', error);
	        setSoundOn(false);
	        setUserHasInteracted(false);
	      });
	    }
	    regularAudioRef.current.loop = true;
	  } else {
	    regularAudioRef.current?.pause();
	  }
	}, [timeLeft, userHasInteracted]);

	useEffect(() => {
	  if (userHasInteracted && timeLeft === '0' && winner === null) {
	    const playPromise = drawAudioRef.current?.play();
	    if (playPromise !== undefined) {
	      playPromise.catch((error) => {
	        console.warn('draw audio blocked', error);
	        setSoundOn(false);
	        setUserHasInteracted(false);
	      });
	    }
	  } else {
	    drawAudioRef.current?.pause();
	  }
	}, [timeLeft, winner, userHasInteracted]);

	useEffect(() => {
	  if (userHasInteracted && timeLeft === '0' && !isSpinning && winner) {
	    const playPromise = winAudioRef.current?.play();
	    if (playPromise !== undefined) {
	      playPromise.catch((error) => {
	        console.warn('winner audio blocked', error);
	        setSoundOn(false);
	        setUserHasInteracted(false);
	      });
	    }
	  } else {
	    winAudioRef.current?.pause();
	  }
	}, [timeLeft, isSpinning, winner, userHasInteracted]);


    return (
    	<>
	    	{ nextDaily && nextDaily[0]?.declareDate && (
	            <Box w="100%" h="100vh">
	            	{ isDark ? 
	            	<DailyWheelLiners 
						className={`animation-wheel-liners ${timeLeft === '0' ? 'stop-animation' : ''}`}
		            	style={{
		            		width: '200%',
		            		height: '200%',
		            		margin: '-15px 0 0 0',
		            		position: 'absolute',
		            		transform: 'translate(-50%, 0)',
		            		left: '50%',
		            		top: '-50%'
		            	}} />
		            : 
		            <DailyWheelLinersWhite 
						className={`animation-wheel-liners ${timeLeft === '0' ? 'stop-animation' : ''}`}
		            	style={{
		            		width: '200%',
		            		height: '200%',
		            		margin: '-15px 0 0 0',
		            		position: 'absolute',
		            		transform: 'translate(-50%, 0)',
		            		left: '50%',
		            		top: '-50%'
		            	}} />
		        	}

		            <Box transition="0.3s all" textAlign="center" w="100%" zIndex="9" position="relative">
		            	
		            	{ responsiveHeader ? (
		            		<Flex justifyContent="space-between" alignItems="center" p="35px 0">
		            			<Box w="max-content">
				            		<NavLink to="/giveaways">
					            		<Image src={logo} alt="Logo LMCT+" w="141px"/>
					            	</NavLink>
					            </Box>

					            <Button variant="secondary-md" w="44px" 
					            	onClick={() => {setSoundOn(!soundOn); setUserHasInteracted(!userHasInteracted);}}>
					            	{ soundOn ? <SoundOnIcon fontSize="24px" color={colorIcon} /> 
					            		: <SoundOffIcon fontSize="24px" color={colorIcon} /> 
					            	}
					            </Button>
		            		</Flex>
		            		) : (
		            		<>
				            	<Box w="max-content" m="0px auto" p="64px 0 40px 0">
				            		<NavLink to="/giveaways">
					            		<Image src={logo} alt="Logo LMCT+" w={{ base: '230px', lg: '310px'}}/>
					            	</NavLink>
					            </Box>

					            <Button variant="secondary-md" position="absolute" w="44px" top="64px" right="60px" 
					            	onClick={() => {setSoundOn(!soundOn); setUserHasInteracted(!userHasInteracted);}}>
					            	{ soundOn ? <SoundOnIcon fontSize="24px" color={colorIcon} /> 
					            		: <SoundOffIcon fontSize="24px" color={colorIcon} /> 
					            	}
					            </Button>
					        </>
			            )}
		            	
		            	<Box transition="0.3s all" display={ ['5', '4', '3', '2', '1', '0'].includes(timeLeft) ? 'none' : 'block'}>
			            	<Text m="8px auto" variant="header" fontSize={{ base: '18px', md: '32px'}}>Coming up next is...</Text>
			            	<Flex key={colorMode} gap="8px" justifyContent="center" mt="12px" alignItems="center">
	                            {Stars}
	                            <Box mt="4px" bg="white" p="4px 10px 2px 13px" borderRadius="16px">
	                            	<Text as="span" variant="header-gradient" fontSize={{ base: '24px', md: '40px'}} pr="4px">$1000 PRIZE GIVEAWAY</Text>
	                            </Box>
	                            {Stars}
	                        </Flex>
                        </Box>

                        <Text
                        	display={ timeLeft !== '0' ? 'block' : 'none' } 
                        	transition="0.3s all"
                        	opacity={ ['5', '4', '3', '2', '1', '0'].includes(timeLeft) ? '1' : '0'}
                        	m="8px auto" variant="header" fontSize={{ base: '18px', md: '32px'}}>Get Ready – Winner Announced in…
                        </Text>

		            </Box>

					<Box position="absolute" 
						w={ timeLeft !== '0' ? '80%' : '90%' }
						h={ timeLeft !== '0' ? '250px' : '300px' } borderRadius="full"
						bg="#14A7FE" 
						display={isDark ? 'block' : 'none'}
						filter={ timeLeft !== '0' ? 'blur(150px)' : 'blur(250px)' }
						transition="0.5s all"
						opacity={ timeLeft === '4' 
							? '0.5' : timeLeft === '3' 
							? '1' : timeLeft === '2' 
							? '0.5' : timeLeft === '1' 
							? '1' : '1' }
						transform={ timeLeft !== '0' ? 'translate(-50%, -50%)' : 'translate(-50%, 30%)' }
						top="50%" left="50%" />

					<Box w="100%" zIndex="9" transform="translate(-50%, -50%)" left="50%" top="50%" position="absolute" textAlign="center"> 
						<Text
							transition="0.3s all"
							opacity={ ['5', '4', '3', '2', '1', '0'].includes(timeLeft) ? '0' : '1'} 
							m="8px auto" variant="header" fontSize={{ base: '18px', md: '32px'}}>Get Ready – Winner Announced in…</Text>
			            
			            <Box 
			            	position="absolute"
			            	left="50%"
			            	top="0"
			            	transition="0.3s all"
    						transform="translate(-50%, 0px)"
			            	opacity={ ['5', '4', '3', '2', '1'].includes(timeLeft) ? '1' : '0'}>
				            <Numdown
				            	endDate={ nextDaily[0]?.declareDate }
				                declareDate={ nextDaily[0]?.declareDate }
				            	timeLeft={timeLeft}
				            	setTimeLeft={setTimeLeft}
				            />
			            </Box>

			            <Box
			            	transition="0.3s all"
			            	opacity={ ['5', '4', '3', '2', '1', '0'].includes(timeLeft) ? '0' : '1'}
			            >
				            <Countdown
				                fontSize={{ base: '24px', md: '42px', lg: '64px'}}
				                endDate={ nextDaily[0]?.declareDate }
				                declareDate={ nextDaily[0]?.declareDate }
				                p={{ base: '12px 30px', md: '18px 62px'}} 
				                right="unset"
				                position="relative"
				                width="fit-content"
				                m="0 auto"
				                borderWidth={responsiveBorderCounter}
				                isLine={true}
				              />
			            </Box>
		            </Box>

		            <Image 
		            	position="absolute" 
		            	transition="1s all"
		            	src={dailyWheelFooter} 
		            	transform="translate(-50%, 0)" 
		            	left="50%"
		            	bottom={ 
		            		['5', '4', '3', '2', '1', '0'].includes(timeLeft) ? '-100%' : '0px' }
		            	maxW="1200px"
		            	w={{ base: '150%', md: '60%'}} 
		            	objectFit="cover" />

		            <Box 
		            	position="absolute" 
		            	transition="1s all"
		            	left="50%"
		            	bottom={ !lastStep ? responsiveBottomWheel : '-200%' }
		            	opacity={timeLeft === '0' ? '1' : '0' }
		            	transform={timeLeft === '0' ? 'translate(-50%, 0%)' : 'translate(-50%, -100%)' }>

		            	<DailyTopWheel1Icon style={{
		            		position: 'absolute',
						    left: '50%',
						    top: 0,
						    transition: '1s all',
						  	transform: timeLeft === '0' ? 'translate(-50%, 33px) scale(1)' : 'translate(-50%, 33px) scale(0.1)'
		            	}} />
		            	
		            	<Text 
		            		color="#FFF"
							fontSize="32px"
							fontStyle="italic"
							fontWeight="990"
							lineHeight="41px"
							letterSpacing="-0.4px"
							transition="0.3s all"
							position="absolute"
							textAlign="right"
						    left="50%"
						    transform="translate(-50%, 240px) rotate(-90deg)"
						    zIndex="9"
						    isTruncated
							whiteSpace="nowrap"
							overflow="hidden"
							textOverflow="ellipsis"
						    width="260px"
						    overflow="hidden"
						    animation={timeLeft === '0' && !isSpinning ? 'winnerDailyWheel 1s linear' : 'none'}
		            	>
		            		{ winner }
		            	</Text>

		            	<Box
						  ref={wheelRef} 
						  className={isSpinning && !winner ? 'animation-daily-spinning' : winner ? 'animation-daily-settling' : 'animation-daily-spinning'}
						>
							{ isDark ? 
							(<DailyWheelIcon style={{ 
							  	transition: '1s all',
							  	transform: timeLeft === '0' ? 'scale(1)' : 'scale(0.1)' }} />)
							: 
							(<DailyWheelWhiteIcon style={{ 
							  	transition: '1s all',
							  	transform: timeLeft === '0' ? 'scale(1)' : 'scale(0.1)' }} />)
							}

							<Box 
								width="1049px"
							    height="1049px"
							    top="53px"
							    left="-63px"
							    opacity="0"
								position="absolute"
								className={ timeLeft === '0' && !winner ? 'animation-daily-spinning-names' : 'animation-daily-spinning-names-settling'}
							>
						      { maskWinners.map((person, index) => {
							    const total = maskWinners.length;
								const angle = (360 / total) * index;
								const isEven = index % 2 === 0;

							    return (
								    <Box
								      	key={index}
								        position="absolute"
								        top="50%"
								        left="50%"
								        w="250px"
								        textAlign="right"
								        transform={`rotate(${angle}deg) translate(0, 350px)`}
								        transformOrigin="center center">
									      <Text
									      	color={ !isDark && !isEven ? '#1FB4FD' : '#FFF' }
											fontSize="32px"

											fontStyle="italic"
											fontWeight="990"
											lineHeight="41px"
											letterSpacing="-0.4px"
											transition="0.3s all"
										    overflow="hidden"
										    whiteSpace="nowrap"
										    transform="rotate(90deg)"
										    animation={isEven ? 'fadeDailyPulse1 1s infinite' : 'fadeDailyPulse2 1.5s infinite'}
									      >
									        {person}
									      </Text>
								    </Box>
							    );
							  })}
						    </Box>
						</Box>

						<DailyTopWheel2Icon style={{
		            		position: 'absolute',
						    left: '50%',
						    top: 0,
						    paddingLeft: '4px',
						    transition: '1s all',
						  	transform: timeLeft === '0' ? 'translate(-50%, 33px) scale(1.04)' : 'translate(-50%, 10px) scale(0.1)'
		            	}} />

		            </Box>

		            <Box 
		            	opacity={ lastStep ? '1' : '0' } 
		            	transition="0.3s all"
		            	w="100%"
		            	zIndex="9" transform="translate(-50%, -50%)" 
		            	left="50%" top="50%" position="absolute" textAlign="center">

						<Flex gap="8px" key={colorMode} justifyContent="center" mt="12px" alignItems="center">
                            {Stars}
                            <Box mt="4px" bg="white" p="4px 10px 2px 11px" borderRadius="16px">
                            	<Text as="span" variant="header-gradient" fontSize={{ base: '24px', md: '40px'}} pr="4px">$1000 WINNER!</Text>
                            </Box>
                            {Stars}
                        </Flex>

                        <Text
							m="44px auto" variant="header" 
							fontSize={{ base: '18px', md: '32px'}}>Congratulations</Text>

						<Text
							m="8px auto" variant="header-gradient" 
							fontSize={{ base: '32px', md: '80px'}}>{ winner }</Text>

						<Text
							m="12px auto" variant="header" 
							textTransform="unset"
							fontSize={{ base: '18px', md: '32px'}}>{ winnerEmail }</Text>

					</Box>

					<Text
						variant="header" 
						position="absolute"
						bottom="40px"
						w="100%"
						textAlign="center"
						opacity={ lastStep ? '1' : '0' } 
						transform="translate(-50%, 0)"
						left="50%" 
						fontSize={{ base: '18px', md: '32px'}}>Loyalty Rewards Club</Text>

	            </Box>
	        )}

			<audio ref={regularAudioRef} src={autoDrawRegularSound} preload="auto" />
			<audio ref={drawAudioRef} src={autoDrawSound} preload="auto" />
			<audio ref={winAudioRef} src={winnerSound} preload="auto" />

			{ winner && 
				<Confetti
			       	width={window.innerWidth}
        			height={window.innerHeight}
        			numberOfPieces={350}
    				recycle={false}
    				gravity={1} 
			    />
			}
	    </>
    );
};


export default DailyWheel;